<template>
  <div>
    <v-form class="pt-6" :class="edit ? 'pt-16' : ''" ref="form" fluid>
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label class="text-bold subtitle-2">{{ $t('email') }}</label>
          <v-tooltip right color="neutral">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="pl-1 info-tooltip" v-bind="attrs" v-on="on"
                >mdi-information-outline
              </v-icon>
            </template>
            <span>{{ $t('email-tooltip') }}</span>
          </v-tooltip>
          <v-text-field
            v-model="user.email"
            :placeholder="$t('email')"
            type="text"
            outlined
            :disabled="!edit"
            @click="changeEmail = true"
            class="pt-2"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label class="text-bold subtitle-2">{{ $t('firstname') }}</label>
          <v-text-field
            v-model="user.firstname"
            :placeholder="$t('firstname')"
            type="text"
            outlined
            :disabled="!edit"
            class="pt-2"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label class="text-bold subtitle-2">{{ $t('lastname') }}</label>
          <v-text-field
            v-model="user.lastname"
            :placeholder="$t('lastname')"
            type="text"
            outlined
            :disabled="!edit"
            class="pt-2"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label class="text-bold subtitle-2">{{ $t('phone') }}</label>
          <v-text-field
            v-model="user.phone"
            :placeholder="$t('phone')"
            type="text"
            outlined
            :disabled="!edit"
            class="pt-2"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label class="text-bold subtitle-2">{{ $t('affiliation') }}</label>
          <v-text-field
            v-model="user.affiliation"
            :placeholder="$t('position')"
            type="text"
            outlined
            :disabled="!edit"
            class="pt-2"
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <label class="text-bold subtitle-2">{{ $t('institution') }}</label>
          <v-tooltip right color="neutral">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="pl-1 info-tooltip" v-bind="attrs" v-on="on"
                >mdi-information-outline
              </v-icon>
            </template>
            <span>{{ $t('institution-tooltip') }}</span>
          </v-tooltip>
          <v-autocomplete
            v-if="$store.getters.roles === 'researcher' && !isNotListedHostingInstitution"
            v-model.trim="user.institutionText"
            :items="researcherInstitutions"
            :placeholder="$t('institution')"
            item-value="name"
            outlined
            return-object
            clearable
            :rules="[value => !!value?.trim()]"
            no-data-text="Press enter button to add a new institution."
            class="pt-2"
            @keyup.enter="addInstitutionTag"
            @update:search-input="typedInstitution = $event"
            :disabled="!edit || $store.getters.roles !== 'researcher'"
          />
          <v-text-field
            v-else-if="$store.getters.roles === 'researcher' && isNotListedHostingInstitution"
            v-model.trim="user.institutionText"
            :placeholder="'Type the hosting institution you want, which is not listed.'"
            type="text"
            outlined
            :rules="[value => !!value?.trim()]"
            class="pt-2"
            clearable
            @click:clear="clearInstitutionText"
            :disabled="!edit || $store.getters.roles !== 'researcher'"
          />
          <v-text-field
            v-else-if="user.institution && user.institution.name"
            class="pt-2"
            v-model="user.institution.name"
            :placeholder="$t('institution')"
            outlined
            disabled
          >
          </v-text-field>
        </v-col>
      </v-row>
      <v-row class="mt-10" v-if="edit">
        <v-col cols="6" class="d-flex justify-end">
          <v-btn
            depressed
            rounded
            x-large
            color="black"
            @click="cancel()"
            outlined
            class="black--text font-weight-bold"
            data-cy="submit"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-col>
        <v-col cols="6">
          <v-btn
            depressed
            rounded
            x-large
            color="neutral"
            @click="updateProfile()"
            class="white--text font-weight-bold"
            data-cy="submit"
            >{{ $t('update') }}
          </v-btn>
        </v-col>
        <v-col cols="2"> </v-col>
      </v-row>
    </v-form>
    <v-divider class="mt-5 mb-5"></v-divider>
    <v-row>
      <v-col cols="12" sm="12" md="6">
        <label class="text-bold subtitle-2">{{ $t('password') }}</label>
        <v-text-field class="pt-2" :placeholder="$t('password')" outlined disabled type="password">
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="12" md="6" class="d-flex align-center">
        <v-btn
          rounded
          depressed
          large
          class="rounded-lg pl-10 pr-10 change-button"
          color="primary200"
          outlined
          @click="changePassword = true"
          >{{ $t('change') }}
        </v-btn>
      </v-col>
    </v-row>
    <div v-if="$store.getters.roles != 'super-admin'">
      <v-divider class="mt-5 mb-5"></v-divider>
      <v-row>
        <v-col cols="12" sm="12" md="6" class="d-flex">
          <v-btn dark color="danger" class="font-weight-bold" rounded @click="deleteUser = true"
            >Delete Account
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!--Change Password Modal-->
    <change-password
      :change-password="changePassword"
      @close="changePassword = false"
    ></change-password>
    <!--Change Email Modal-->
    <change-email :change-email="changeEmail" @close="changeEmail = false"></change-email>
    <!--Delete User Modal-->
    <delete-account :delete-user="deleteUser" @close="deleteUser = false"></delete-account>
  </div>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus';
import ChangeEmail from '@/components/shared/profile/ChangeEmail';
import ChangePassword from '@/components/shared/profile/ChangePassword';
import service from '@/services/base-service';
import profile from '@/services/profile-service';
import DeleteAccount from '@/components/shared/profile/DeleteAccount';

export default {
  name: 'ProfileInfo',
  components: {
    DeleteAccount,
    ChangeEmail,
    ChangePassword,
  },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [util, service, profile],
  data() {
    return {
      user: {
        email: null,
        firstname: null,
        lastname: null,
        accept: null,
        institution: null,
        institutionText: null,
        affiliation: null,
      },
      changePassword: false,
      changeEmail: false,
      deleteUser: false,
      edit: this.editable,
      isNotListedHostingInstitution: false,
      researcherInstitutions: [],
      typedInstitution: '',
    };
  },
  methods: {
    loadProfile() {
      return this.getProfile()
        .then(res => (this.user = res.data))
        .finally(() => {
          this.loading = false;
        });
    },
    updateProfile() {
      if (!this.$refs.form.validate()) {
        EventBus.$emit('snackbar', {
          text: 'Institution is required',
          color: 'warning',
        });
        return;
      }
      EventBus.$emit('waiting', true);
      this.putProfile(this.user)
        .then(() => {
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('profile-updated'),
            color: 'primary200',
          });
          this.$store.commit('setFullName', `${this.user.firstname} ${this.user.lastname}`);
          this.$emit('cancel');
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.$emit('cancel');
    },
    addInstitutionTag() {
      const selectedInstitution = this.user.institutionText || this.typedInstitution?.trim();
      console.log('Typed Institution:', selectedInstitution);

      const exists = this.researcherInstitutions.some(inst => inst.name === selectedInstitution);

      if (!exists && selectedInstitution) {
        this.user.institutionText = selectedInstitution;
        this.isNotListedHostingInstitution = true;
      }
    },
    clearInstitutionText() {
      this.isNotListedHostingInstitution = false;
      this.user.institutionText = null;
    },
  },
  mounted() {
    this.loadProfile();

    this.axios.get('/api/v1/hosting-institutions/researcher/by-name?name=').then(res => {
      this.researcherInstitutions = res.data.content;
    });
  },
  watch: {
    editable(value) {
      this.edit = value;
      if (!value) {
        this.loadProfile().then(() => this.$refs.form.resetValidation());
      }
    },
  },
};
</script>

<style scoped></style>
